import { useState, useCallback } from 'react';
import axiosInstance from '@/lib/axios';
import { toast } from 'sonner';

export interface PostOffice {
  name: string;
  branchType: string;
  deliveryStatus: string;
  district: string;
  state: string;
  country: string;
  pincode: string;
  region: string;
  circle: string;
  division: string;
  block: string;
}

export interface AddressData {
  pincode: string;
  country: string;
  state: string;
  district: string;
  taluka: string;
  states: string[];
  districts: string[];
  talukas: string[];
  postOffices: PostOffice[];
}

interface UseAddressAutofillReturn {
  isLoading: boolean;
  error: string | null;
  fetchAddress: (pincode: string) => Promise<AddressData | null>;
  clearError: () => void;
}

export function useAddressAutofill(): UseAddressAutofillReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAddress = useCallback(async (pincode: string): Promise<AddressData | null> => {
    if (!pincode || pincode.length < 4) {
      setError('Please enter a valid pincode');
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.get(`/api/address/pincode/${pincode}`);

      if (response.data.success) {
        const data = response.data;

        // Default to first values if available
        const defaultState = data.states?.[0] || '';
        const defaultDistrict = data.districts?.[0] || '';
        const defaultTaluka = data.talukas?.[0] || '';

        return {
          pincode: data.pincode,
          country: data.country,
          state: defaultState,
          district: defaultDistrict,
          taluka: defaultTaluka,
          states: data.states || [],
          districts: data.districts || [],
          talukas: data.talukas || [],
          postOffices: data.postOffices || [],
        };
      }

      setError('Address not found for this pincode');
      return null;
    } catch (err) {
      const axiosError = err as { response?: { data?: { error?: string } } };
      const errorMessage = axiosError.response?.data?.error || 'Failed to fetch address details';
      setError(errorMessage);
      toast.error(errorMessage);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    isLoading,
    error,
    fetchAddress,
    clearError,
  };
}
